import { useCallback } from 'react';

import { ExtractedTableJson } from '@frontend/api';
import { AssistanceIcon, Button, useToast } from '@frontend/legacy-components';

import { useRetryExtractedTable } from './hooks/useRetryExtractedTable';

type RetryExtractedTableActionProps = {
  rawTableData: ExtractedTableJson;
  /**
   * Called synchronously on click, before the retry has resolved. The dashed placeholder
   * annotation and progress toast that follow both need the modal out of the way.
   */
  onRetried: () => void;
};

/**
 * Dismisses a BEAMS-extracted table and resubmits its page region for reprocessing,
 * closing the modal immediately so the user can see the resulting placeholder and toast.
 */
export const RetryExtractedTableAction = ({
  rawTableData,
  onRetried,
}: RetryExtractedTableActionProps) => {
  const { retryExtractedTable } = useRetryExtractedTable();
  const toast = useToast();

  const handleClick = useCallback(() => {
    onRetried();
    retryExtractedTable(rawTableData).catch(() => {
      toast({
        title: 'Failed to retry table extraction',
        status: 'error',
      });
    });
  }, [onRetried, retryExtractedTable, rawTableData, toast]);

  return (
    <Button
      colorScheme="default"
      variant="link"
      size="md"
      icon={<AssistanceIcon />}
      onClick={handleClick}
    >
      Retry
    </Button>
  );
};
